import React, { useState, useEffect } from 'react'
import {
  Card, Form, Button, Select, InputNumber, Input,
  Checkbox, Divider, message, Alert, Row, Col, Tag, Skeleton,
} from 'antd'
import {
  ArrowLeftOutlined, ThunderboltOutlined, SafetyCertificateOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { AllocationStatus } from '@/types'
import { useMasterData } from '@/context/MasterDataContext'
import { Invoice, invoiceStore } from './invoiceStore'
import { allocationStore } from '../Allocation/allocationStore'
import { generateCode } from '@/utils/codeGenerator'
import { NotificationEngine } from '@/utils/notificationEngine'
import { SUPPLIER_STATE, STATE_OPTIONS } from '@/utils/gstStates'
import { deriveInvoiceStatus } from '@/utils/invoiceHelpers'
import { resolveGeneratorTariff, resolveConsumerTariff } from '@/utils/tariffResolver'

const fmt = (n: number) =>
  `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

interface AllocOption {
  label: string
  value: string
  units: number
  tariff: number | null
  entity: string
  period: string
}

const InvoiceForm: React.FC = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = !!id

  const { gstConfigs, generators, consumers, tariffs } = useMasterData()

  const gstTotals = Array.from(new Set(
    // cgstRate/sgstRate/igstRate are Postgres `numeric` columns and arrive as
    // strings — `+` on those concatenates instead of adding (e.g. "9.00" +
    // "9.00" = "9.009.00"), so coerce before combining.
    gstConfigs.filter(g => g.status === 'active').map(g => Number(g.igstRate) || (Number(g.cgstRate) + Number(g.sgstRate)))
  )).sort((a, b) => a - b)
  const gstSlabOptions = gstTotals.map(t => ({ label: `${t}%`, value: t }))

  const activeGenerators = generators.filter(g => g.status === 'active')
  const activeConsumers  = consumers.filter(c => c.status === 'active')

  const [saving, setSaving] = useState(false)
  const [invoiceType,       setInvoiceType]       = useState<'generator' | 'consumer' | null>(null)
  const [selectedEntityId,  setSelectedEntityId]  = useState<string>('')
  const [selectedAllocDetail, setSelectedAllocDetail] = useState<AllocOption | null>(null)
  const [baseAmount,    setBaseAmount]    = useState<number>(0)
  const [gstSlab,       setGstSlab]       = useState<number>(gstTotals.includes(18) ? 18 : (gstTotals[0] ?? 18))
  const [placeOfSupply, setPlaceOfSupply] = useState<string>(SUPPLIER_STATE)

  // Stay in sync with invoiceStore's load state — on a fresh page load
  // straight at an edit URL, the store's cache starts empty, so the prefill
  // effect below used to run once, find nothing, and give up permanently
  // (its deps were just `[id]`), silently leaving the "edit" form blank
  // instead of showing the invoice actually being edited. This tick forces
  // that effect to retry once the store's real data has arrived.
  const [invoiceStoreTick, setInvoiceStoreTick] = useState(0)
  useEffect(() => {
    const bump = () => setInvoiceStoreTick(t => t + 1)
    if (!invoiceStore.loaded) invoiceStore.refresh().then(bump)
    window.addEventListener(invoiceStore.eventName, bump)
    return () => window.removeEventListener(invoiceStore.eventName, bump)
  }, [])

  // TN CGST+SGST split only up to 33% total; above 33% goes as IGST directly
  const isIntra     = gstSlab <= 33 && placeOfSupply === SUPPLIER_STATE
  const cgstRate    = isIntra ? gstSlab / 2 : 0
  const sgstRate    = isIntra ? gstSlab / 2 : 0
  const igstRate    = isIntra ? 0 : gstSlab
  const gstAmount   = baseAmount * gstSlab / 100
  const totalAmount = baseAmount + gstAmount

  // ── Real allocations (Approved / Locked only) ──────────────────────────────
  const approvedAllocs = allocationStore.getAll().filter(a =>
    a.status === AllocationStatus.APPROVED || a.status === AllocationStatus.LOCKED
  )

  const allocOptions: AllocOption[] = (() => {
    if (!selectedEntityId || !invoiceType) return []
    // totalGenerated/allocatedUnits are Postgres `numeric` columns and arrive
    // as strings — coerce once here so every downstream use (toLocaleString
    // formatting, arithmetic, saved record) is guaranteed a real number
    // instead of silently skipping formatting.
    //
    // Tariff prefers whatever was already frozen on the allocation record —
    // ca.tariff/a.generatorTariff — over a fresh Tariff Master lookup. That
    // stored value is what the lead actually agreed to (traced back through
    // Mapping to their Proposal's Sakthi PF Charge — see leadTariff.ts) and
    // what the allocation was actually approved/reconciled at; re-deriving it
    // fresh here would let the invoice silently bill a different rate than
    // what the customer was quoted, if Tariff Master changes afterward.
    // Falls back to a live Master lookup only when nothing's frozen yet
    // (legacy allocations from before this existed).
    if (invoiceType === 'generator') {
      return approvedAllocs
        .filter(a => a.generatorId === selectedEntityId)
        .map(a => {
          const units = Number(a.totalGenerated) || 0
          const frozenTariff = Number(a.generatorTariff)
          return {
            label: `${a.id} — ${a.generatorName} (${a.period}) — ${units.toLocaleString('en-IN')} kWh`,
            value: a.id,
            units,
            tariff: frozenTariff > 0 ? frozenTariff : resolveGeneratorTariff(tariffs, selectedEntityId),
            entity: a.generatorName,
            period: a.period,
          }
        })
    } else {
      return approvedAllocs.flatMap(a =>
        (a.consumerAllocations ?? [])
          .filter(c => c.id === selectedEntityId)
          .map(c => {
            const units = Number(c.allocatedUnits) || 0
            const frozenTariff = Number(c.tariff)
            return {
              label: `${a.id} — ${c.name} (${a.period}) — ${units.toLocaleString('en-IN')} kWh`,
              value: `${a.id}__${c.id}`,
              units,
              tariff: frozenTariff > 0 ? frozenTariff : resolveConsumerTariff(tariffs, c.id, a.generatorId),
              entity: c.name,
              period: a.period,
            }
          })
      )
    }
  })()

  const entityOptions = invoiceType === 'generator'
    ? activeGenerators.map(g => ({ label: `${g.name} (${g.code})`, value: g.id }))
    : activeConsumers.map(c => ({ label: `${c.name} (${c.htscNumber || c.id})`, value: c.id }))

  const yyyyMM = new Date().toISOString().slice(0, 7)
  const autoInvoiceNum = invoiceType
    ? `SP/${invoiceType === 'generator' ? 'G' : 'C'}/${yyyyMM}/${String(invoiceStore.getAll().length + 1).padStart(3, '0')}`
    : ''

  // No active Shakthi Power Tariff resolved for the selected allocation's
  // entity — block invoice creation instead of silently billing at ₹0 (or,
  // previously, a hardcoded fallback rate).
  const tariffMissing = !!selectedAllocDetail && selectedAllocDetail.tariff == null

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleTypeChange = (v: 'generator' | 'consumer') => {
    setInvoiceType(v)
    setSelectedEntityId('')
    setSelectedAllocDetail(null)
    setBaseAmount(0)
    form.setFieldsValue({ entityId: undefined, allocationId: undefined, baseAmount: undefined, period: undefined })
  }

  const handleEntityChange = (entityId: string) => {
    setSelectedEntityId(entityId)
    setSelectedAllocDetail(null)
    setBaseAmount(0)
    form.setFieldsValue({ allocationId: undefined, baseAmount: undefined, period: undefined })
  }

  const handleAllocSelect = (val: string) => {
    const opt = allocOptions.find(a => a.value === val)
    if (!opt) return
    setSelectedAllocDetail(opt)
    const base = opt.units * (opt.tariff ?? 0)
    setBaseAmount(base)
    form.setFieldsValue({ baseAmount: base, period: opt.period })
  }

  // ── Edit prefill ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!id) return
    const existing = invoiceStore.get(id)
    if (!existing) return
    setInvoiceType(existing.type)
    // existing.baseAmount is a Postgres `numeric` column and arrives as a
    // string — feeding it straight into this numeric state made every later
    // `baseAmount + gstAmount` string-concatenate instead of add, corrupting
    // totalAmount on save even when the user only changed an unrelated field.
    const existingBase = Number(existing.baseAmount) || 0
    setBaseAmount(existingBase)
    const slab = existingBase > 0
      ? Math.round((Number(existing.gstAmount) / existingBase) * 100)
      : gstSlab
    setGstSlab(slab)
    // Preserve the invoice's real Place of Supply — forcing it back to the
    // supplier's own state on every edit was silently reclassifying real
    // interstate (IGST) invoices as intrastate (CGST+SGST).
    setPlaceOfSupply(existing.placeOfSupply || SUPPLIER_STATE)
    // Restore entity/allocation linkage so re-saving without touching Step 2
    // doesn't blank entity/allocationId/units/tariff — especially important
    // once the originally-linked allocation is no longer Approved/Locked and
    // so no longer offered in the Step 2 dropdown at all.
    setSelectedEntityId(existing.entityId || '')
    if (existing.units != null && existing.tariff != null) {
      setSelectedAllocDetail({
        label: '', value: existing.allocationId || '',
        units: existing.units, tariff: existing.tariff,
        entity: existing.entity, period: existing.period,
      })
    }
    form.setFieldsValue({
      type: existing.type,
      entityId: existing.entityId || undefined,
      allocationId: existing.allocationId || undefined,
      invoiceNumber: existing.invoiceNumber,
      period: existing.period,
      baseAmount: existingBase,
      gstSlab: slab,
      placeOfSupply: existing.placeOfSupply || SUPPLIER_STATE,
      dueDate: existing.dueDate,
      irn: existing.irn ?? undefined,
      ewayBill: existing.ewayBill ?? undefined,
      reverseCharge: existing.reverseCharge ?? false,
      notes: existing.notes || '',
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, invoiceStoreTick])

  // ── Save ───────────────────────────────────────────────────────────────────
  const onFinish = async (values: any) => {
    if (tariffMissing) {
      message.error('No active Shakthi Power Tariff found for this entity — set one up in Tariff Master first.')
      return
    }
    setSaving(true)
    try {
    const todayStr = new Date().toISOString().slice(0, 10)

    // Snapshot bill-to details from the selected generator / consumer master record
    const entityRecord: any = invoiceType === 'generator'
      ? generators.find(g => g.id === selectedEntityId)
      : consumers.find(c => c.id === selectedEntityId)
    const clientAddress = String(entityRecord?.companyAddress || entityRecord?.plantAddress || '')
      .split(',').map((s: string) => s.trim()).filter(Boolean)
    // Plain HT/HTSC number, no label prefix — matches what the auto-generator
    // (AllocationList.tsx's generateInvoicesForAllocation) writes for both
    // invoice types. Re-saving an auto-generated invoice through this manual
    // form used to overwrite that plain value with "HTSC No - ..." — wrong
    // outright for generators (HTSC is consumer-specific HT-connection
    // terminology) and inconsistent with un-edited invoices for consumers.
    const customerRef = invoiceType === 'generator'
      ? (entityRecord?.htServiceNumber || selectedEntityId)
      : (entityRecord?.htscNumber || selectedEntityId)

    const allocationIdRaw = values.allocationId || selectedAllocDetail?.value || ''
    const allocationId = allocationIdRaw ? String(allocationIdRaw).split('__')[0] : ''

    let invoiceNum = values.invoiceNumber || ''
    if (!isEditing && !invoiceNum) {
      try {
        invoiceNum = await generateCode('invoices')
      } catch (err) {
        console.error('[invoice] generateCode(invoices) failed, using fallback format:', err)
        message.warning("Couldn't reach Code Master for an invoice number — used a fallback format instead.")
        invoiceNum = autoInvoiceNum
      }
    }

    // Preserve payment history on edit — recomputing paidAmount/status from
    // scratch on every save was silently wiping out recorded payments.
    const existingRecord = isEditing && id ? invoiceStore.get(id) : undefined
    const paidAmount = existingRecord ? Math.min(existingRecord.paidAmount || 0, totalAmount) : 0
    const status = existingRecord ? deriveInvoiceStatus(existingRecord.status, totalAmount, paidAmount) : 'issued'

    const record: Omit<Invoice, 'id'> = {
      invoiceNumber: invoiceNum || autoInvoiceNum,
      type: (values.type || invoiceType || 'consumer') as Invoice['type'],
      entity: selectedAllocDetail?.entity || '',
      entityId: selectedEntityId,
      allocationId,
      period: values.period || selectedAllocDetail?.period || '',
      units: selectedAllocDetail?.units,
      tariff: selectedAllocDetail?.tariff ?? undefined,
      sac: values.sac || '998312',
      placeOfSupply,
      baseAmount,
      cgstAmount: baseAmount * cgstRate / 100,
      sgstAmount: baseAmount * sgstRate / 100,
      igstAmount: baseAmount * igstRate / 100,
      gstAmount,
      totalAmount,
      paidAmount,
      status,
      dueDate: values.dueDate || '',
      irn: values.irn || null,
      ewayBill: values.ewayBill || null,
      gstr1Filed: false,
      gstr3bFiled: false,
      issueDate: todayStr,
      clientGstNo: entityRecord?.gstin || undefined,
      clientAddress: clientAddress.length ? clientAddress : undefined,
      customerRef,
      reverseCharge: !!values.reverseCharge,
      notes: values.notes || '',
    }
    const saved = invoiceStore.save({ id: isEditing ? id : undefined, ...record })

    if (!isEditing) {
      NotificationEngine.trigger({
        event: 'INVOICE_GENERATED',
        title: `Invoice Generated: ${saved.invoiceNumber}`,
        message: `Invoice ${saved.invoiceNumber} of ₹${saved.totalAmount.toLocaleString('en-IN')} generated for ${saved.entity}. Due: ${saved.dueDate}.`,
        recipient: 'Accounts Team',
      })
    }

    message.success(`Invoice ${isEditing ? 'updated' : 'created'} successfully!`)
    navigate('/invoices')
    } finally {
      setSaving(false)
    }
  }

  // Editing a record that hasn't arrived from invoiceStore yet (fresh page
  // load / hard refresh straight at this URL): show a placeholder instead of
  // the real form. Rendering the form now would let someone submit it before
  // the prefill effect above has run, silently overwriting the real
  // invoice's fields with the form's empty defaults.
  if (isEditing && !invoiceStore.get(id!) && !invoiceStore.loaded) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 2 }} style={{ marginBottom: 24 }} />
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/invoices')} />
        <div>
          <h2 className="page-title">{isEditing ? 'Edit Invoice' : 'Create New Invoice'}</h2>
          <p className="page-subtitle">GST-compliant invoice with IRN, e-Way Bill, and GSTR filing support</p>
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={onFinish}>

        {/* ── Step 1: Type + Entity ─────────────────────────── */}
        <Card
          title={<span style={{ fontWeight: 700 }}>Step 1 — Invoice Type &amp; Entity</span>}
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={8}>
              <Form.Item label="Invoice Type" name="type" rules={[{ required: true }]}>
                <Select
                  placeholder="Select type..."
                  onChange={handleTypeChange}
                  options={[
                    { label: 'Generator Invoice — based on units supplied', value: 'generator' },
                    { label: 'Consumer Invoice — based on allocated units',  value: 'consumer' },
                  ]}
                />
              </Form.Item>
            </Col>

            {invoiceType && (
              <Col xs={24} sm={8}>
                <Form.Item
                  label={invoiceType === 'generator' ? 'Select Generator' : 'Select Consumer'}
                  name="entityId"
                  rules={[{ required: true, message: 'Select an entity' }]}
                >
                  <Select
                    showSearch
                    placeholder={invoiceType === 'generator' ? 'Choose generator...' : 'Choose consumer...'}
                    options={entityOptions}
                    onChange={handleEntityChange}
                    filterOption={(i, o) =>
                      (o?.label as string)?.toLowerCase().includes(i.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            )}

            <Col xs={24} sm={8}>
              <Form.Item
                label="Invoice Number"
                name="invoiceNumber"
                tooltip={{ title: 'Auto-generated on save if left blank', icon: <InfoCircleOutlined /> }}
              >
                <Input
                  placeholder={autoInvoiceNum || 'Auto-generated'}
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  prefix={
                    <Tag style={{ margin: '0 4px 0 -8px', borderRadius: 4, fontSize: 11 }}>
                      {invoiceType === 'generator' ? 'G' : invoiceType === 'consumer' ? 'C' : '?'}
                    </Tag>
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={8}>
              <Form.Item label="Billing Period" name="period">
                <Input
                  placeholder="Auto-filled from allocation"
                  readOnly
                  style={{ background: '#F8FAFC', fontFamily: 'JetBrains Mono, monospace' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Step 2: Allocation ───────────────────────────── */}
        {invoiceType && selectedEntityId && (
          <Card
            title={<span style={{ fontWeight: 700 }}>Step 2 — Link Allocation</span>}
            style={{ marginBottom: 16 }}
          >
            {allocOptions.length === 0 ? (
              <Alert
                type="warning" showIcon
                message="No approved allocations found"
                description={`No Approved or Locked allocations exist for the selected ${invoiceType}. Approve an allocation first from the Unit Allocation module.`}
              />
            ) : (
              <>
                <Form.Item
                  label={invoiceType === 'generator' ? 'Generator Allocation' : 'Consumer Allocation'}
                  name="allocationId"
                  rules={[{ required: true, message: 'Select an allocation' }]}
                >
                  <Select
                    placeholder="Select allocation..."
                    options={allocOptions.map(a => ({ label: a.label, value: a.value }))}
                    onChange={handleAllocSelect}
                  />
                </Form.Item>

                {selectedAllocDetail && tariffMissing && (
                  <Alert
                    type="error" showIcon
                    message={`No active Shakthi Power Tariff found for ${selectedAllocDetail.entity} — set one up in Tariff Master first.`}
                  />
                )}

                {selectedAllocDetail && (
                  <div style={{
                    display: 'flex', gap: 20, flexWrap: 'wrap',
                    padding: '12px 18px', background: '#EFF4FF',
                    borderRadius: 10, border: '1px solid #BFDBFE',
                    marginTop: tariffMissing ? 12 : 0,
                  }}>
                    {[
                      { label: 'Entity',  value: selectedAllocDetail.entity },
                      { label: 'Period',  value: selectedAllocDetail.period },
                      { label: 'Units',   value: `${selectedAllocDetail.units.toLocaleString('en-IN')} kWh` },
                      { label: 'Tariff',  value: selectedAllocDetail.tariff != null ? `₹${selectedAllocDetail.tariff}/kWh` : 'Not configured' },
                      { label: 'Base',    value: selectedAllocDetail.tariff != null ? fmt(selectedAllocDetail.units * selectedAllocDetail.tariff) : '—' },
                    ].map(item => (
                      <div key={item.label}>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#3B82F6', marginBottom: 3 }}>
                          {item.label}
                        </div>
                        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13, color: '#1E40AF' }}>
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card>
        )}

        {/* Steps 3–5 reveal only after type + entity are chosen */}
        {(isEditing || (invoiceType && selectedEntityId)) && (
          <>
            {/* ── Step 3: Amount & GST ───────────────────────── */}
            <Card
              title={<span style={{ fontWeight: 700 }}>Step 3 — Amount &amp; GST</span>}
              style={{ marginBottom: 16 }}
            >
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={8}>
                  <Form.Item label="Base Amount (₹)" name="baseAmount" rules={[{ required: true }]}>
                    <InputNumber
                      style={{ width: '100%', fontFamily: 'JetBrains Mono, monospace' }}
                      min={0} step={0.01} precision={2} prefix="₹"
                      value={baseAmount || undefined}
                      onChange={v => setBaseAmount(v ?? 0)}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="GST Slab"
                    name="gstSlab"
                    initialValue={gstSlab}
                    tooltip={{ title: 'Total rate from GST Master — auto-split CGST+SGST (intra) or IGST (inter).', icon: <InfoCircleOutlined /> }}
                  >
                    <Select options={gstSlabOptions} onChange={(v: number) => setGstSlab(v)} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label="Due Date" name="dueDate" rules={[{ required: true }]}>
                    <Input type="date" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Place of Supply" name="placeOfSupply" rules={[{ required: true }]} initialValue={SUPPLIER_STATE}>
                    <Select
                      showSearch
                      placeholder="Select state..."
                      options={STATE_OPTIONS.map(s => ({ label: s, value: s }))}
                      onChange={(v: string) => setPlaceOfSupply(v)}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Supply Type (GST)">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 32 }}>
                      {isIntra
                        ? <Tag color="green" style={{ fontWeight: 600 }}>Intra-state → CGST {cgstRate}% + SGST {sgstRate}%</Tag>
                        : <Tag color="blue"  style={{ fontWeight: 600 }}>Inter-state → IGST {igstRate}%</Tag>
                      }
                      <span style={{ fontSize: 11.5, color: '#8A93A6' }}>Supplier: {SUPPLIER_STATE}</span>
                    </div>
                  </Form.Item>
                </Col>
              </Row>

              {baseAmount > 0 && (
                <div style={{ background: '#F1F4F9', borderRadius: 10, padding: '16px 20px', marginTop: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24 }}>
                    {[
                      { label: 'Base Amount', value: fmt(baseAmount), color: '#0B1426' },
                      ...(isIntra
                        ? [
                            { label: `CGST ${cgstRate}%`, value: fmt(baseAmount * cgstRate / 100), color: '#4A5568' },
                            { label: `SGST ${sgstRate}%`, value: fmt(baseAmount * sgstRate / 100), color: '#4A5568' },
                          ]
                        : [{ label: `IGST ${igstRate}%`, value: fmt(baseAmount * igstRate / 100), color: '#4A5568' }]
                      ),
                      { label: 'Total GST',     value: fmt(gstAmount),   color: '#2563EB' },
                      { label: 'Invoice Total', value: fmt(totalAmount),  color: '#059669' },
                    ].map(item => (
                      <div key={item.label} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#8A93A6', marginBottom: 4 }}>
                          {item.label}
                        </div>
                        <div style={{
                          fontFamily: 'JetBrains Mono, monospace',
                          fontSize: item.label === 'Invoice Total' ? 20 : 15,
                          fontWeight: 700, color: item.color,
                        }}>
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* ── Step 4: GST Compliance ─────────────────────── */}
            <Card
              title={
                <span style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <SafetyCertificateOutlined style={{ color: '#059669' }} /> Step 4 — GST Compliance
                </span>
              }
              style={{ marginBottom: 16 }}
            >
              <Form.Item name="reverseCharge" valuePropName="checked">
                <Checkbox>
                  <span style={{ fontWeight: 600 }}>Reverse Charge Applicable</span>
                  <span style={{ fontSize: 12, color: '#8A93A6', marginLeft: 8 }}>(Liability shifts to recipient)</span>
                </Checkbox>
              </Form.Item>

              <Divider />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#4A5568', marginBottom: 8 }}>
                    e-Invoice IRN
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Form.Item name="irn" style={{ flex: 1, marginBottom: 0 }}>
                      <Input placeholder="Auto-generated after submission" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }} readOnly />
                    </Form.Item>
                    <Button icon={<ThunderboltOutlined />} onClick={() => {
                      form.setFieldValue('irn', 'IRN-' + Math.random().toString(36).slice(2, 12).toUpperCase())
                      message.success('IRN generated')
                    }}>
                      Generate IRN
                    </Button>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#4A5568', marginBottom: 8 }}>
                    e-Way Bill
                  </div>
                  <Form.Item name="ewayBill" style={{ marginBottom: 0 }}>
                    <Input placeholder="Enter e-Way Bill number (if applicable)" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }} />
                  </Form.Item>
                </div>
              </div>

              <Alert
                type="info" showIcon
                message="GSTR-1/3B Export"
                description="After saving, export this invoice from Invoice Detail for GSTR-1 and GSTR-3B filing."
                style={{ marginTop: 16, borderRadius: 8 }}
              />
            </Card>

            {/* ── Notes ──────────────────────────────────────── */}
            <Card title={<span style={{ fontWeight: 700 }}>Notes</span>} style={{ marginBottom: 16 }}>
              <Form.Item name="notes" style={{ marginBottom: 0 }}>
                <Input.TextArea rows={2} placeholder="Payment terms, special instructions…" />
              </Form.Item>
            </Card>

            <Divider />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Button onClick={() => navigate('/invoices')} disabled={saving}>Cancel</Button>
              <Button type="primary" htmlType="submit" disabled={tariffMissing} loading={saving}>
                {isEditing ? 'Update Invoice' : 'Create Invoice'}
              </Button>
            </div>
          </>
        )}
      </Form>
    </div>
  )
}

export default InvoiceForm
